import { FC } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Combobox } from "@/components/Interface/FormFields/Combobox/Combobox";
import { FormField } from "@/components/Interface/FormFields/FormField/FormField";
import type { Country } from "@/constants/countries";
import { useGetSortedCountries } from "@/hooks/useGetSortedCountries";
import {
  DACH_HOUSE_NUMBER_REGEX,
  DEFAULT_TEXT_FIELD_REGEX,
  getPostalCodePattern,
  INTERNATIONAL_NAME_REGEX,
} from "@/utils/constants";

import { BillingAccountFormValues } from "./billingAccountForm";

interface Props {
  /** Business customers bill to a company, so its name is mandatory. */
  isCompanyNameRequired: boolean;
  disabled?: boolean;
}

/** Bezeichnung, recipient and postal address of a Vertragskonto. */
export const BillingAccountAddressFields: FC<Props> = ({
  isCompanyNameRequired,
  disabled = false,
}) => {
  const { t } = useTranslation("portal", {
    keyPrefix: "user-account.billingAccounts.modal.fields",
  });
  const { t: tGlobal } = useTranslation();
  const {
    register,
    control,
    watch,
    formState: { errors },
  } = useFormContext<BillingAccountFormValues>();
  const sortedCountries = useGetSortedCountries();
  const selectedCountry = watch("country");

  const required = tGlobal("translation:label.validation.required");
  const textPattern = {
    value: DEFAULT_TEXT_FIELD_REGEX,
    message: tGlobal("translation:Common.generalForm.error.genericRegexError"),
  };
  const namePattern = { ...textPattern, value: INTERNATIONAL_NAME_REGEX };
  const fieldProps = { register, errors, variant: "dark" as const };

  return (
    <div className="grid grid-cols-1 gap-2.5 tablet:grid-cols-2 tablet:gap-x-3">
      <div className="flex flex-col gap-2 tablet:col-span-2">
        <FormField<BillingAccountFormValues>
          id="billing-account-alias"
          name="alias"
          label={t("alias")}
          options={{
            required,
            maxLength: 40,
            pattern: textPattern,
            disabled,
          }}
          {...fieldProps}
        />
        <p className="text-xs leading-md text-foreground-primary-inverse">
          {t("aliasHint")}
        </p>
      </div>
      <FormField<BillingAccountFormValues>
        id="billing-account-first-name"
        name="firstName"
        label={t("firstName")}
        options={{ required, maxLength: 40, pattern: namePattern, disabled }}
        {...fieldProps}
      />
      <FormField<BillingAccountFormValues>
        id="billing-account-last-name"
        name="lastName"
        label={t("lastName")}
        options={{ required, maxLength: 40, pattern: namePattern, disabled }}
        {...fieldProps}
      />
      <FormField<BillingAccountFormValues>
        id="billing-account-company-name"
        className="tablet:col-span-2"
        name="companyName"
        label={t("companyName")}
        options={{
          required: isCompanyNameRequired && required,
          maxLength: 40,
          pattern: textPattern,
          disabled,
        }}
        {...fieldProps}
      />
      <FormField<BillingAccountFormValues>
        id="billing-account-additional-address"
        className="tablet:col-span-2"
        name="additionalAddress"
        label={t("additionalAddress")}
        options={{
          required: false,
          maxLength: 40,
          pattern: textPattern,
          disabled,
        }}
        {...fieldProps}
      />
      <FormField<BillingAccountFormValues>
        id="billing-account-street"
        name="street"
        label={t("street")}
        options={{ required, maxLength: 40, pattern: textPattern, disabled }}
        {...fieldProps}
      />
      <FormField<BillingAccountFormValues>
        id="billing-account-house-number"
        name="houseNumber"
        label={t("houseNumber")}
        options={{
          required,
          maxLength: 10,
          pattern: { ...textPattern, value: DACH_HOUSE_NUMBER_REGEX },
          disabled,
        }}
        {...fieldProps}
      />
      <FormField<BillingAccountFormValues>
        id="billing-account-zip"
        name="zip"
        label={t("zip")}
        options={{
          required,
          maxLength: 10,
          pattern: {
            ...textPattern,
            value: getPostalCodePattern(selectedCountry),
          },
          disabled,
        }}
        {...fieldProps}
      />
      <FormField<BillingAccountFormValues>
        id="billing-account-city"
        name="city"
        label={t("city")}
        options={{ required, maxLength: 40, pattern: textPattern, disabled }}
        {...fieldProps}
      />
      <div className="tablet:col-span-2">
        <Controller
          control={control}
          name="country"
          render={({ field }) => (
            <Combobox<Country>
              dataTestId="billing-account-country"
              field={field}
              variant="dark"
              label={t("country")}
              displayValue={(country?: Country) =>
                country
                  ? tGlobal(`translation:countries.${country.ALPHA3}`)
                  : ""
              }
              options={sortedCountries}
              placeholder={t("country")}
              disabled={disabled}
            />
          )}
        />
      </div>
    </div>
  );
};
